/**
 * EVC Deposit Dialog Content
 *
 * Simplified deposit dialog matching the EvcRepayDialogContent pattern.
 * Uses wagmi-evc hooks for EVC batch transactions.
 * Supports:
 * - Direct Deposit (ERC20 with Permit2)
 * - Native Deposit (HYPE wrap & deposit)
 * - Swap & Deposit (wallet → swap → deposit)
 *
 * IMPORTANT: For wallet swaps (fromVault = zeroAddress), we use skipSweepDepositOut=true
 * when calling the Euler Swap API. This tells the API to OMIT the final deposit() call.
 * We then manually add a DepositTransactionItem that deposits to the SUBACCOUNT (position),
 * not the wallet address.
 */

'use client'

import { useCallback, useMemo, useState, useEffect } from 'react'
import { useAccount, useConfig, useReadContract, useReadContracts, useWriteContract, useBalance } from 'wagmi'
import { useWaitForTransactionReceiptUniversal } from '@/domain/hooks/useWaitForTransactionReceiptUniversal'
import { type Address, parseUnits, formatUnits, erc20Abi, zeroAddress, encodeFunctionData } from 'viem'
import { useQueryClient } from '@tanstack/react-query'
import { eEarnVaultImplementationAbi } from 'abis/evm/eEarnVaultImplementation'
import { eVaultImplementationAbi } from 'abis/evm/eVaultImplementation'
import { eulerVaultConnectorAbi } from 'abis/evm/eulerVaultConnector'

import { E_LEND_CHAIN_ID, EVC_ADDRESS } from '@/config/euler-contracts'
import { MAX_UINT160 } from '@/config/consts'
import { invalidateBalancesCache } from '@/domain/cache'
import { getPositionLabel } from '@/domain/euler/positions/usePositionName'
import { usePositionNames } from '@/domain/state'
import { DialogPanel } from '@/features/dialogs/common/components/DialogPanel'
import { Dialog, DialogContent, DialogTitle } from '@/ui/atoms/dialog/Dialog'
import { MultiPanelDialog } from '@/features/dialogs/common/components/MultiPanelDialog'
import { TransactionOverviewDetailsItem } from '@/features/dialogs/common/components/transaction-overview/TransactionOverviewDetailsItem'
import { ActionRow } from '@/features/actions/components/action-row/ActionRow'
import { TokenIcon } from '@/ui/atoms/token-icon/TokenIcon'
import { assets } from '@/ui/assets'
import type { ActionHandlerState } from '@/features/actions/logic/types'
import { getSubAccount } from '@/domain/evc'
import { TransactionLabel, useSimulateEVCBatchCall } from '@eulerxyz/wagmi-evc'
import { buildPermit2BatchRequest } from '@/domain/euler/evc/buildPermit2BatchRequest'
import type {
  TransactionItems,
  WrapTransactionItem,
  SwapMulticallTransactionItem,
  SkimTransactionItem,
  DepositTransactionItem,
} from '@eulerxyz/wagmi-evc'
import { EVaultDetailsData } from '../../logic/useEVaultDetails'
import { Token } from '@/domain/types/Token'
import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { CheckedAddress } from '@/domain/types/CheckedAddress'
import { usePermit2Preflight } from '@/domain/euler/evc/usePermit2Preflight'
import { getTokenImage } from '@/ui/assets/tokens'
import { TransactionErrorPanel } from '@/ui/molecules/transaction-error-panel/TransactionErrorPanel'
import { EulerSwapResponse } from '@/domain/euler/swap'
import { DepositSuccessView } from './components/DepositSuccessView'
import { NormalizedUnitNumber } from '@/domain/types/NumericValues'
import { NATIVE_HYPE_ADDRESS, WHYPE_ADDRESS_HYPEREVM } from '@/domain/euler/nativeHype'
import { isUserRejection } from '@/lib/errors'
import { TimeoutErrorDisplay } from '@/features/actions/components/TimeoutErrorDisplay'
import { isTimeoutError, useActionTimeout } from '@/features/actions/logic/useActionTimeout'
import { useSubaccountStore, getFirstAvailableSubaccountId } from '@/domain/euler/subaccounts'
import { useSubaccountControllers } from '@/domain/euler/subaccounts/useSubaccountControllers'
import { getSubAccountAddress, FALLBACK_SUBACCOUNT_RANGE } from '@/domain/euler/shared/subAccounts'
import { getVaultMarket } from '@/features/evault-details/data/products'
import { useELendsList } from '@/domain/euler/elends/useELendInfo'
import { useTrackedLendBorrowAction } from '@/features/analytics'
import { Typography } from '@/ui/atoms/typography/Typography'
import { SupplyCapDetail } from '@/ui/molecules/supply-cap-detail/SupplyCapDetail'
import { usePreviewSupplyAPY } from '@/features/elend/logic/usePreviewSupplyAPY'
import { ApyChange } from '@/ui/atoms/apy-change/ApyChange'
import { cn } from '@/ui/utils/style'
import { ArrowRight, ChevronDown, Loader2, Plus } from 'lucide-react'
import { TokenImage } from '@/ui/atoms/token-image/TokenImage'
import { useDebounce } from '@/utils/useDebounce'
import { normalizeDecimalInput } from '@/utils/decimalInput'
import { BigBlocksBanner } from '@/ui/molecules/big-blocks-banner/BigBlocksBanner'

interface DepositDestination {
  id: number
  label: string
  isNew: boolean
  subAccountAddress: Address
  debtSymbols: string[]
  marketName?: string
  healthFactor: number | null
  totalDebtUsd: number
}

interface EvcDepositDialogContentProps {
  closeDialog: () => void
  evault: EVaultDetailsData
  depositAmount: string
  depositToken: Token
  isNativeDeposit: boolean
  swapQuote?: EulerSwapResponse | null
  /** Where tokenIn is coming from for swap flows. */
  swapSourceKind?: 'wallet' | 'euler' | 'pooled' | 'euler-same-underlying'
  /** Source vault for tokenIn when swapping (tokenIn vault). Required for wallet swaps. */
  swapSourceVault?: Address
  /** Source sub-account for eLend sources (unused in EvcDepositDialogContent, but passed for consistency) */
  sourceSubAccount?: Address
  expectedShares?: string
}

export function EvcDepositDialogContent({
  closeDialog,
  evault,
  depositAmount,
  depositToken,
  isNativeDeposit,
  swapQuote,
}: EvcDepositDialogContentProps) {
  const { address } = useAccount()
  const config = useConfig()
  const chainId = E_LEND_CHAIN_ID
  const queryClient = useQueryClient()
  const { getPositionName } = usePositionNames()

  // State
  const [isSuccess, setIsSuccess] = useState(false)
  const [selectorOpen, setSelectorOpen] = useState(false)
  const [selectedDestinationId, setSelectedDestinationId] = useState<number | null>(null)

  // Editable amount input (only for non-swap direct deposits)
  const isSwapFlow = !!swapQuote
  const [amountInput, setAmountInput] = useState(depositAmount)
  const { debouncedValue: debouncedAmount, isDebouncing } = useDebounce(amountInput, amountInput, { delay: 500 })
  // Use the debounced value for all calculations; swap flows always use the original prop
  const effectiveDepositAmount = isSwapFlow ? depositAmount : debouncedAmount

  // ========== SUB-ACCOUNT DESTINATION LOGIC ==========
  const { activeSubaccountIds } = useSubaccountStore()

  const idsToQuery = useMemo(() => {
    const fallbackIds = Array.from({ length: FALLBACK_SUBACCOUNT_RANGE }, (_, i) => i)
    return [...new Set([...fallbackIds, ...activeSubaccountIds])]
  }, [activeSubaccountIds])

  const { subaccountControllers, isLoading: isControllersLoading } = useSubaccountControllers({
    owner: address,
    subaccountIds: idsToQuery,
    enabled: !!address,
  })

  // Query collaterals for each sub-account to filter out truly empty positions
  const collateralContracts = useMemo(() => {
    if (!address) return []
    return idsToQuery.map((subAccountId) => ({
      address: EVC_ADDRESS as Address,
      abi: eulerVaultConnectorAbi,
      functionName: 'getCollaterals' as const,
      args: [getSubAccountAddress(address, subAccountId)] as const,
      chainId,
    }))
  }, [address, idsToQuery, chainId])

  const { data: collateralData, isLoading: isCollateralsLoading } = useReadContracts({
    contracts: collateralContracts,
    query: {
      enabled: collateralContracts.length > 0,
      staleTime: 30_000,
    },
  })

  const subaccountCollaterals = useMemo(() => {
    const map = new Map<number, Address[]>()
    if (!collateralData) return map
    idsToQuery.forEach((subAccountId, index) => {
      const result = collateralData[index]
      if (result?.status === 'success') {
        map.set(subAccountId, result.result as Address[])
      } else {
        map.set(subAccountId, [])
      }
    })
    return map
  }, [collateralData, idsToQuery])

  const { vaults: elendVaults } = useELendsList(chainId)

  const vaultMetadataMap = useMemo(() => new Map(elendVaults.map((v) => [v.address.toLowerCase(), v])), [elendVaults])

  // Sub-accounts with controllers (need debt queries)
  const subAccountsWithDebt = useMemo(() => {
    if (!address || !subaccountControllers) return []
    const result: { subAccountId: number; controllers: Address[]; subAccountAddress: Address }[] = []
    for (const [subAccountId, controllers] of subaccountControllers) {
      if (controllers.length === 0) continue
      result.push({
        subAccountId,
        controllers,
        subAccountAddress: getSubAccountAddress(address, subAccountId),
      })
    }
    return result
  }, [address, subaccountControllers])

  // Query debtOf for every controller on each sub-account
  const debtContracts = useMemo(() => {
    return subAccountsWithDebt.flatMap(({ controllers, subAccountAddress }) =>
      controllers.map((controller) => ({
        address: controller,
        abi: eVaultImplementationAbi,
        functionName: 'debtOf' as const,
        args: [subAccountAddress] as const,
        chainId,
      })),
    )
  }, [subAccountsWithDebt, chainId])

  const { data: debtData } = useReadContracts({
    contracts: debtContracts,
    query: {
      enabled: debtContracts.length > 0,
      staleTime: 30_000,
    },
  })

  // Build deposit destinations — show all positions with debt or collateral
  const destinations = useMemo((): DepositDestination[] => {
    if (!address || !subaccountControllers) return []

    const dests: DepositDestination[] = []

    for (const [subAccountId, controllers] of subaccountControllers) {
      if (controllers.length === 0) {
        const collaterals = subaccountCollaterals.get(subAccountId) ?? []
        if (collaterals.length === 0) continue

        dests.push({
          id: subAccountId,
          label: getPositionLabel(subAccountId, getPositionName(address, subAccountId)),
          isNew: false,
          subAccountAddress: getSubAccountAddress(address, subAccountId),
          debtSymbols: [],
          marketName: undefined,
          healthFactor: null,
          totalDebtUsd: 0,
        })
        continue
      }

      const controllerMarket = getVaultMarket(controllers[0]!)

      let flatOffset = 0
      for (const s of subAccountsWithDebt) {
        if (s.subAccountId === subAccountId) break
        flatOffset += s.controllers.length
      }

      const debtSymbols: string[] = []
      let totalDebtUsd = 0

      for (let i = 0; i < controllers.length; i++) {
        const ctrl = controllers[i]!
        const meta = vaultMetadataMap.get(ctrl.toLowerCase())
        const symbol = meta?.assetSymbol || 'UNKNOWN'
        const decimals = meta?.decimals ?? 18
        const price = meta?.assetPrice ?? 0

        const debtRaw = debtData?.[flatOffset + i]?.result ? (debtData[flatOffset + i]!.result as bigint) : 0n
        if (debtRaw > 0n) {
          totalDebtUsd += Number(formatUnits(debtRaw, decimals)) * price
          debtSymbols.push(symbol)
        }
      }

      dests.push({
        id: subAccountId,
        label: getPositionLabel(subAccountId, getPositionName(address, subAccountId)),
        isNew: false,
        subAccountAddress: getSubAccountAddress(address, subAccountId),
        debtSymbols,
        marketName: controllerMarket?.name,
        healthFactor: null,
        totalDebtUsd,
      })
    }

    // "New Position" option
    const idsInUse = [...subaccountControllers.entries()]
      .filter(([id, c]) => c.length > 0 || (subaccountCollaterals.get(id)?.length ?? 0) > 0)
      .map(([id]) => id)

    const nextAvailableId = getFirstAvailableSubaccountId(idsInUse)
    if (nextAvailableId !== -1 && dests.length < FALLBACK_SUBACCOUNT_RANGE) {
      dests.push({
        id: nextAvailableId,
        label: 'New Position',
        isNew: true,
        subAccountAddress: getSubAccountAddress(address, nextAvailableId),
        debtSymbols: [],
        marketName: undefined,
        healthFactor: null,
        totalDebtUsd: 0,
      })
    }

    return dests
  }, [address, subaccountControllers, subaccountCollaterals, vaultMetadataMap, subAccountsWithDebt, debtData, getPositionName])

  const isDestinationsLoading = isControllersLoading || isCollateralsLoading

  // Auto-select first destination (prefer sub-account 0, fallback to first available)
  useEffect(() => {
    if (selectedDestinationId !== null || destinations.length === 0) return
    const subAccount0 = destinations.find((d) => d.id === 0 && !d.isNew)
    if (subAccount0) {
      setSelectedDestinationId(subAccount0.id)
    } else {
      setSelectedDestinationId(destinations[0]!.id)
    }
  }, [destinations, selectedDestinationId])

  const selectedDestination = useMemo(
    () => destinations.find((d) => d.id === selectedDestinationId) ?? destinations[0] ?? null,
    [destinations, selectedDestinationId],
  )

  const handleSelectDestination = useCallback((dest: DepositDestination) => {
    setSelectedDestinationId(dest.id)
    setSelectorOpen(false)
  }, [])

  // Position address derived from selected destination
  const position = useMemo(() => {
    if (selectedDestination) return selectedDestination.subAccountAddress
    if (!address) return undefined
    return getSubAccount(address, 0)
  }, [address, selectedDestination])

  // For swap flows: depositAmount is the OUTPUT amount, depositToken is the INPUT token
  // This is confusing but that's how it's passed from the parent
  // We need to show the INPUT amount being spent
  const displayAmount = useMemo(() => {
    if (isSwapFlow && swapQuote) {
      // Swap flow: show the input amount (what user is actually spending)
      return formatUnits(BigInt(swapQuote.amountIn), swapQuote.tokenIn.decimals)
    }
    // Direct deposit: use the provided deposit amount
    return effectiveDepositAmount
  }, [isSwapFlow, swapQuote, effectiveDepositAmount])

  const displayToken = useMemo(() => {
    if (isSwapFlow && swapQuote) {
      // Swap flow: show the input token (what user is spending)
      return {
        symbol: swapQuote.tokenIn.symbol,
        address: swapQuote.tokenIn.address,
        decimals: swapQuote.tokenIn.decimals,
      }
    }
    // Direct deposit: use the deposit token
    return depositToken
  }, [isSwapFlow, swapQuote, depositToken])

  // Parse amount for transactions
  const amountBigInt = useMemo(() => {
    if (isSwapFlow && swapQuote) {
      try {
        return BigInt(swapQuote.amountIn)
      } catch {
        return 0n
      }
    }
    try {
      return parseUnits(effectiveDepositAmount || '0', depositToken.decimals)
    } catch {
      return 0n
    }
  }, [isSwapFlow, swapQuote, effectiveDepositAmount, depositToken.decimals])

  // For expected shares calculation:
  // - Swap flow: use swap OUTPUT amount (what goes into vault) from swapQuote
  // - Direct deposit: use deposit amount
  const amountForSharesCalculation = useMemo(() => {
    if (isSwapFlow && swapQuote) {
      try {
        // Use swap output amount (already in base units)
        return BigInt(swapQuote.amountOut)
      } catch {
        return 0n
      }
    }
    // Direct deposit: parse the deposit amount with correct decimals
    try {
      return parseUnits(effectiveDepositAmount || '0', depositToken.decimals)
    } catch {
      return 0n
    }
  }, [isSwapFlow, swapQuote, effectiveDepositAmount, depositToken.decimals])

  // For swap flows the token being spent may differ from depositToken (e.g. pooled market)
  const balanceCheckAddress =
    isSwapFlow && swapQuote ? (swapQuote.tokenIn.address as Address) : (depositToken.address as Address)
  const balanceCheckSymbol = isSwapFlow && swapQuote ? swapQuote.tokenIn.symbol : depositToken.symbol
  const balanceCheckDecimals = isSwapFlow && swapQuote ? swapQuote.tokenIn.decimals : depositToken.decimals

  // Fetch user's wallet balance (for ERC20)
  const { data: walletBalanceRaw } = useReadContract({
    address: balanceCheckAddress,
    abi: erc20Abi,
    functionName: 'balanceOf',
    args: address ? [address] : undefined,
    chainId,
    query: {
      enabled: !!address && !isNativeDeposit,
      refetchInterval: 10_000,
    },
  })

  // Fetch native HYPE balance
  const { data: nativeBalance } = useBalance({
    address,
    chainId,
    query: {
      enabled: !!address && isNativeDeposit,
      refetchInterval: 10_000,
    },
  })

  // Use native HYPE balance or ERC20 balance based on deposit type
  // Reserve 0.001 HYPE for gas costs when using native HYPE
  const GAS_RESERVE_RAW = parseUnits('0.001', 18)
  const nativeBalanceAfterGasReserve =
    nativeBalance?.value !== undefined
      ? nativeBalance.value > GAS_RESERVE_RAW
        ? nativeBalance.value - GAS_RESERVE_RAW
        : 0n
      : undefined
  const activeWalletBalanceRaw = isNativeDeposit ? nativeBalanceAfterGasReserve : walletBalanceRaw
  const activeSymbol = isNativeDeposit ? 'HYPE' : balanceCheckSymbol
  const walletBalance =
    activeWalletBalanceRaw !== undefined ? Number(formatUnits(activeWalletBalanceRaw, balanceCheckDecimals)) : 0

  const insufficientBalance = activeWalletBalanceRaw !== undefined && amountBigInt > activeWalletBalanceRaw
  const canDeposit = !!address && amountBigInt > 0n && !insufficientBalance && !!position && !isDebouncing

  // Always fetch expected shares ourselves to ensure correct decimal handling
  // For swap flows, use the swap OUTPUT amount
  const { data: expectedSharesRaw } = useReadContract({
    address: evault.address,
    abi: eEarnVaultImplementationAbi,
    functionName: 'previewDeposit',
    args: amountForSharesCalculation > 0n ? [amountForSharesCalculation] : undefined,
    chainId,
    query: {
      enabled: amountForSharesCalculation > 0n,
    },
  })

  const calculatedExpectedShares = useMemo(() => {
    // Always use the freshly fetched value with correct decimals
    if (expectedSharesRaw !== undefined) {
      return formatUnits(expectedSharesRaw, evault.decimals)
    }
    return undefined
  }, [expectedSharesRaw, evault.decimals])

  const { newAPY: previewNewAPY, isLoading: isPreviewAPYLoading } = usePreviewSupplyAPY({
    vaultAddress: evault.address as Address,
    changeAmount: amountForSharesCalculation,
    changeType: 'deposit',
    chainId: E_LEND_CHAIN_ID,
    enabled: amountForSharesCalculation > 0n,
  })

  // Detect if this is a native HYPE swap (user selected HYPE, swap expects WHYPE)
  // For native HYPE swaps: skip Permit2, use msg.value, let swap multicall handle wrapping
  const isNativeHypeSwap = useMemo(() => {
    return (
      isSwapFlow &&
      depositToken.address.toLowerCase() === NATIVE_HYPE_ADDRESS.toLowerCase() &&
      swapQuote?.tokenIn.address.toLowerCase() === WHYPE_ADDRESS_HYPEREVM.toLowerCase()
    )
  }, [isSwapFlow, depositToken.address, swapQuote])

  // Build transactions
  const transactions = useMemo((): TransactionItems => {
    if (!address || !position || amountBigInt === 0n) return []

    // For native HYPE deposits, the asset MUST be WHYPE (the wrapped token)
    // WETHTransactionFactory requires the wrapped asset, not the native asset
    const asset = {
      address: (isNativeDeposit ? evault.underlyingAsset : depositToken.address) as Address,
      name: isNativeDeposit ? 'WHYPE' : depositToken.name,
      symbol: isNativeDeposit ? 'WHYPE' : depositToken.symbol,
      decimals: depositToken.decimals,
    }

    const txs: TransactionItems = []

    // Swap + Deposit flow (wallet → swap → vault)
    if (isSwapFlow && swapQuote && swapQuote.swap) {
      const swap = swapQuote.swap

      const tokenInAsset = {
        address: swapQuote.tokenIn.address as Address,
        name: swapQuote.tokenIn.name,
        symbol: swapQuote.tokenIn.symbol,
        decimals: swapQuote.tokenIn.decimals,
      }

      const tokenOutAsset = {
        address: swapQuote.tokenOut.address as Address,
        name: swapQuote.tokenOut.name,
        symbol: swapQuote.tokenOut.symbol,
        decimals: swapQuote.tokenOut.decimals,
      }

      // For wallet swaps (vaultIn = zeroAddress), the Euler Swap API now correctly
      // OMITS the deposit call thanks to skipSweepDepositOut=true parameter.
      // We add our own skim transaction to the SUBACCOUNT (position).

      const swapCalls = swap.multicallItems.map((item: any) => item.data)

      // For ERC-20 swaps, prepend transferFromSender to pull tokens via Permit2 (spender=swapper).
      // Native HYPE swaps skip this — they wrap HYPE→WHYPE and ERC20-transfer to the swapper
      // in handleExecute instead, bypassing Permit2 entirely.
      let calls: `0x${string}`[] = swapCalls
      if (!isNativeHypeSwap) {
        const transferFromSenderData = encodeFunctionData({
          abi: [
            {
              inputs: [
                { name: 'token', type: 'address' },
                { name: 'amount', type: 'uint256' },
                { name: 'to', type: 'address' },
              ],
              name: 'transferFromSender',
              outputs: [],
              stateMutability: 'nonpayable',
              type: 'function',
            },
          ] as const,
          functionName: 'transferFromSender',
          args: [
            swapQuote.tokenIn.address as Address,
            amountBigInt > MAX_UINT160 ? MAX_UINT160 : amountBigInt,
            swap.swapperAddress,
          ],
        })
        calls = [transferFromSenderData, ...swapCalls]
      }

      // Swap multicall (no deposit included thanks to skipSweepDepositOut)
      const swapTx: SwapMulticallTransactionItem = {
        label: TransactionLabel.SWAP_MULTICALL,
        from: address,
        to: evault.address as Address,
        asset: tokenInAsset,
        fromVault: zeroAddress,
        toVault: evault.address as Address,
        accountTo: position,
        receiver: evault.address as Address,
        assetIn: tokenInAsset,
        assetOut: tokenOutAsset,
        amountIn: swapQuote.amountIn,
        amountOut: swapQuote.amountOutMin,
        swapperAddress: swap.swapperAddress,
        swapperData: swap.swapperData,
        calls,
      }
      txs.push(swapTx)

      // Step 3: Add a skim transaction to convert the swapped tokens into vault shares
      // The swap sends tokenOut to the vault, then we skim it to create shares for the subaccount
      const skimTx: SkimTransactionItem = {
        label: TransactionLabel.SKIM,
        from: position, // Skim on behalf of subaccount
        to: position, // Skim to subaccount (receives vault shares)
        asset: tokenOutAsset,
        vault: evault.address as Address,
        value: swapQuote.amountOutMin, // Skim the full swap output
        isMax: true,
      }
      txs.push(skimTx)

      return txs
    }

    // Native deposit: wrap HYPE → WHYPE and deposit via skim
    if (isNativeDeposit) {
      const wrapTx: WrapTransactionItem = {
        label: TransactionLabel.WRAP,
        from: address,
        to: position,
        asset,
        vault: evault.address as Address,
        value: amountBigInt.toString(),
        totalAssets: '0',
        totalShares: '0',
      }
      txs.push(wrapTx)
      return txs
    }

    // Direct ERC20 deposit (no swap)
    // For Permit2 deposits, the permit is handled separately in handleExecute
    const depositTx: DepositTransactionItem = {
      label: TransactionLabel.DEPOSIT,
      from: address,
      to: position,
      asset,
      vault: evault.address as Address,
      value: amountBigInt.toString(),
    }
    txs.push(depositTx)
    return txs
  }, [
    address,
    position,
    depositToken,
    evault.address,
    evault.underlyingAsset,
    amountBigInt,
    isNativeDeposit,
    isNativeHypeSwap,
    isSwapFlow,
    swapQuote,
  ])

  // Permit2 preflight check
  // - For swap flows: Permit2 spender is the swapper (for wallet → swapper transfer), token is swapQuote.tokenIn
  // - For direct ERC20: Permit2 spender is the vault, token is depositToken
  // - For native deposits or native HYPE swaps: No Permit2 needed
  // IMPORTANT: spender must NEVER be the EVC — see EVC.call() + Permit2 drain vulnerability
  const permit2Token = useMemo(() => {
    if (!isNativeDeposit && !isNativeHypeSwap) {
      if (isSwapFlow && swapQuote) {
        // Check if input token is different from deposit token (pooled market case)
        if (swapQuote.tokenIn.address.toLowerCase() !== depositToken.address.toLowerCase()) {
          // Pooled market swap: create a Token from swapQuote.tokenIn (e.g., hyHYPE)
          return new Token({
            address: CheckedAddress(swapQuote.tokenIn.address),
            symbol: TokenSymbol(swapQuote.tokenIn.symbol),
            name: swapQuote.tokenIn.name,
            decimals: swapQuote.tokenIn.decimals,
            unitPriceUsd: '0', // Price not needed for permit
          })
        }
        // Wallet swap: use depositToken as-is (has correct price data, etc.)
      }
      return depositToken
    }
    return undefined
  }, [isNativeDeposit, isNativeHypeSwap, isSwapFlow, swapQuote, depositToken])

  const permit2Spender = isSwapFlow
    ? (swapQuote?.swap?.swapperAddress as Address | undefined)
    : (evault.address as Address)

  const permit2 = usePermit2Preflight({
    token: permit2Token,
    spender: permit2Spender,
    amount: amountBigInt,
    enabled: (isSwapFlow || !isNativeDeposit) && !isNativeHypeSwap && canDeposit && !!address && !!permit2Spender,
  })

  // Simulate the EVC batch
  const {
    data: simulationData,
    isLoading: isSimulating,
    error: simulationError,
  } = useSimulateEVCBatchCall({
    config,
    transactions,
    value: isNativeDeposit || isNativeHypeSwap ? amountBigInt : undefined,
    query: {
      enabled:
        canDeposit && transactions.length > 0 && (isNativeDeposit || isNativeHypeSwap || permit2.status === 'ready'),
      retry: false,
    },
  })

  const finalRequest = simulationData?.request

  // Write contract
  const { writeContract, isPending: isWriting, error: writeError, data: txHash } = useWriteContract()

  // Wait for transaction
  const {
    isLoading: isWaiting,
    isSuccess: isConfirmed,
    error: waitError,
  } = useWaitForTransactionReceiptUniversal({
    hash: txHash,
  })
  const { markSubmitted } = useTrackedLendBorrowAction({
    actionType: 'deposit',
    token: depositToken.symbol,
    market: evault.address,
    marketType: 'vault',
    amount: effectiveDepositAmount,
    txHash,
    isConfirmed,
    error: writeError || waitError || simulationError || permit2.error,
  })

  // Execute the batch
  const handleExecute = useCallback(async () => {
    if (!finalRequest) {
      return
    }

    markSubmitted()

    try {
      // For native deposits or native HYPE swaps: execute without Permit2
      if (isNativeDeposit || isNativeHypeSwap) {
        // For native HYPE swaps, we need to manually build the batch with wrap + transfer
        if (isNativeHypeSwap && swapQuote && swapQuote.swap) {
          const swap = swapQuote.swap
          const baseEvcCalls = (((finalRequest as Record<string, unknown>)?.args as unknown[])?.[0] as unknown[]) || []
          let fullEvcCalls = []

          // 1. Wrap HYPE → WHYPE (mints to EVC since msg.sender in batch is EVC)
          const wrapCall = {
            targetContract: WHYPE_ADDRESS_HYPEREVM,
            onBehalfOfAccount: address!,
            value: amountBigInt,
            data: '0xd0e30db0' as `0x${string}`, // WHYPE.deposit() function selector
          }
          fullEvcCalls.push(wrapCall)

          // 2. Transfer WHYPE from EVC to swapper using ERC20.transfer
          const transferAmount = amountBigInt > MAX_UINT160 ? MAX_UINT160 : amountBigInt
          const transferData = encodeFunctionData({
            abi: erc20Abi,
            functionName: 'transfer',
            args: [swap.swapperAddress, transferAmount],
          })

          const transferCall = {
            targetContract: WHYPE_ADDRESS_HYPEREVM,
            onBehalfOfAccount: address!,
            value: 0n,
            data: transferData,
          }
          fullEvcCalls.push(transferCall)

          // 3. Add the base multicall (swap + skim)
          fullEvcCalls = [...fullEvcCalls, ...baseEvcCalls]

          const executionRequest = {
            ...finalRequest,
            args: [fullEvcCalls],
            value: amountBigInt,
          }
          writeContract(executionRequest as any)
        } else {
          // For eLend native deposits (no swap), use the request as-is (uses skim)
          const executionRequest = {
            ...finalRequest,
            value: amountBigInt,
          }
          writeContract(executionRequest as any)
        }
        return
      }

      // For ERC20 swaps and direct deposits: prepend Permit2 to the batch
      if (simulationData) {
        const executionRequest = buildPermit2BatchRequest({
          simulationData: { ...simulationData, request: finalRequest as Record<string, unknown> },
          permitTx: permit2.status === 'ready' ? permit2.permitTx : null,
          chainId,
        })
        writeContract(executionRequest as any)
      } else {
        writeContract(finalRequest as any)
      }
    } catch {
      // Silently fail - errors are handled by wagmi hooks
    }
  }, [
    finalRequest,
    simulationData,
    writeContract,
    permit2.permitTx,
    permit2.status,
    chainId,
    isNativeDeposit,
    isNativeHypeSwap,
    amountBigInt,
    swapQuote,
    address,
    markSubmitted,
  ])

  // ========== ACTION TIMEOUT TRACKING ==========
  // Track timeout for deposit execution to provide feedback when operations hang
  const depositTimeout = useActionTimeout({
    timeoutMs: 15000, // 15 seconds for deposit operations
    isLoading: isWriting || isWaiting || isSimulating,
    isSuccess: isConfirmed,
    error: writeError || waitError || simulationError || permit2.error,
  })

  // Wrapped execute handler that tracks timeout
  const handleExecuteWithTimeout = useCallback(() => {
    depositTimeout.handleActionClick(handleExecute)
  }, [depositTimeout, handleExecute])

  // Determine action handler states
  const approveStatus: ActionHandlerState['status'] = useMemo(() => {
    if (isNativeDeposit || isNativeHypeSwap) return 'success' // Skip Permit2 for native deposits and native HYPE swaps
    if (permit2.status === 'idle') return 'disabled'
    if (permit2.status === 'needs_approval') return 'ready'
    if (permit2.status === 'approving' || permit2.isApproving) return 'loading'
    return 'success'
  }, [permit2.status, permit2.isApproving, isNativeDeposit, isNativeHypeSwap])

  const signStatus: ActionHandlerState['status'] = useMemo(() => {
    if (isNativeDeposit || isNativeHypeSwap) return 'success' // Skip Permit2 for native deposits and native HYPE swaps
    if (permit2.status === 'idle') return 'disabled'
    if (permit2.status === 'needs_approval' || permit2.status === 'approving' || permit2.isApproving) return 'disabled'
    if (permit2.status === 'needs_signature') return 'ready'
    if (permit2.status === 'signing' || permit2.isSigning) return 'loading'
    return 'success'
  }, [permit2.status, permit2.isApproving, permit2.isSigning, isNativeDeposit, isNativeHypeSwap])

  const executeStatus: ActionHandlerState['status'] = useMemo(() => {
    // Active transaction states take priority - never allow retry while tx is pending
    if (isWriting || isWaiting) return 'loading'

    // Check prerequisites - must be met even after timeout
    if (!canDeposit) return 'disabled'

    if (isNativeDeposit || isNativeHypeSwap) {
      if (!finalRequest) {
        return isSimulating ? 'loading' : 'disabled'
      }
      return 'ready'
    }

    // ERC20: need permit2 flow + simulation data
    if (permit2.status !== 'ready') return 'disabled'
    if (!finalRequest) {
      return isSimulating ? 'loading' : 'disabled'
    }
    return 'ready'
  }, [permit2.status, finalRequest, canDeposit, isSimulating, isWriting, isWaiting, isNativeDeposit, isNativeHypeSwap])

  // Mark as success when confirmed
  useEffect(() => {
    if (isConfirmed && !isSuccess) {
      setIsSuccess(true)
    }
  }, [isConfirmed, isSuccess])

  const invalidateSubaccounts = useSubaccountStore((state) => state.invalidate)

  // Invalidate queries after successful transaction
  useEffect(() => {
    if (isConfirmed && address && position) {
      invalidateBalancesCache({
        queryClient,
        chainId,
        address,
        position,
        vaultAddress: evault.address as Address,
      })
      invalidateSubaccounts()
    }
  }, [isConfirmed, address, position, chainId, queryClient, evault.address, invalidateSubaccounts])

  const depositTokenImage = getTokenImage(TokenSymbol(depositToken.symbol)) || depositToken.logoURI

  // Only show errors that aren't user rejections
  const displayError = useMemo(() => {
    // Timeout error takes priority for better UX feedback
    if (depositTimeout.isTimedOut) {
      return depositTimeout.displayError
    }
    const errors = [permit2.error, simulationError, writeError, waitError].filter(Boolean)
    return errors.find((err) => !isUserRejection(err)) || null
  }, [permit2.error, simulationError, writeError, waitError, depositTimeout.isTimedOut, depositTimeout.displayError])

  // Success state
  if (isSuccess) {
    // For swap flows, show the OUTPUT token (what was deposited), not the input
    const successToken =
      isSwapFlow && swapQuote
        ? new Token({
            address: CheckedAddress(swapQuote.tokenOut.address),
            symbol: TokenSymbol(swapQuote.tokenOut.symbol),
            name: swapQuote.tokenOut.name,
            decimals: swapQuote.tokenOut.decimals,
            unitPriceUsd: '0', // Price not needed for success view
          })
        : depositToken

    const successAmount =
      isSwapFlow && swapQuote
        ? NormalizedUnitNumber(formatUnits(BigInt(swapQuote.amountOutMin), swapQuote.tokenOut.decimals))
        : NormalizedUnitNumber(displayAmount)

    return (
      <DepositSuccessView
        token={successToken}
        amount={successAmount}
        onProceed={closeDialog}
        proceedText="View vault details"
      />
    )
  }

  return (
    <MultiPanelDialog>
      <DialogTitle>Deposit to {evault.name}</DialogTitle>

      <DialogPanel>
        {/* Deposit Summary */}
        <div className="flex flex-col gap-4">
          <div className="flex items-center justify-between rounded-xl border border-primary/20 bg-primary/5 p-4">
            <div className="flex items-center gap-3">
              {depositTokenImage && (
                <TokenImage
                  src={depositTokenImage}
                  alt={displayToken.symbol}
                  width={32}
                  height={32}
                  className="rounded-full"
                  tokenAddress={depositToken.address}
                />
              )}
              <div className="flex flex-col">
                <span className="text-muted-foreground text-xs">{isSwapFlow ? 'Swapping' : 'Deposit Amount'}</span>
                <span className="font-medium text-sm text-white">{displayToken.symbol}</span>
              </div>
            </div>
            {isSwapFlow ? (
              <span className="font-semibold text-lg text-primary">
                {Number(displayAmount).toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 6,
                })}
              </span>
            ) : (
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  inputMode="decimal"
                  placeholder="0.00"
                  value={amountInput}
                  disabled={isWriting || isWaiting}
                  onChange={(e) => {
                    const normalized = normalizeDecimalInput(e.target.value)
                    if (normalized !== null) setAmountInput(normalized)
                  }}
                  className="w-32 bg-transparent text-right font-semibold text-lg text-primary outline-none placeholder:text-primary/40 disabled:opacity-50"
                />
                {activeWalletBalanceRaw !== undefined && (
                  <button
                    type="button"
                    onClick={() =>
                      setAmountInput(formatUnits(activeWalletBalanceRaw, balanceCheckDecimals))
                    }
                    className="rounded-md bg-primary/10 px-2 py-0.5 font-medium text-primary text-xs hover:bg-primary/20"
                  >
                    MAX
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Arrow */}
          <div className="-my-2 flex justify-center">
            <div className="flex h-8 w-8 items-center justify-center rounded-full border border-white/10 bg-white/5">
              <ArrowRight className="h-4 w-4 rotate-90 text-muted-foreground" />
            </div>
          </div>

          {/* Destination selector */}
          <div className="overflow-hidden rounded-xl border border-primary/10 bg-primary/[0.02]">
            <Dialog open={selectorOpen} onOpenChange={setSelectorOpen}>
              <button
                type="button"
                className="flex w-full cursor-pointer items-center justify-between rounded-xl p-4 transition-colors hover:bg-primary/[0.04]"
                onClick={() => setSelectorOpen(true)}
              >
                <div className="flex items-center gap-3">
                  {selectedDestination ? (
                    <>
                      <div className="flex h-8 w-8 items-center justify-center rounded-full border border-primary/30 bg-primary/10">
                        {selectedDestination.isNew ? (
                          <Plus className="h-4 w-4 text-primary" />
                        ) : (
                          <span className="font-bold text-primary text-xs">#{selectedDestination.id}</span>
                        )}
                      </div>
                      <div className="flex flex-col items-start gap-0.5">
                        <span className="text-muted-foreground text-xs">Deposit to</span>
                        <span className="font-medium text-sm text-white">{selectedDestination.label}</span>
                        {selectedDestination.isNew ? (
                          <span className="text-primary/70 text-xs">New sub-account</span>
                        ) : selectedDestination.debtSymbols.length > 0 ? (
                          <div className="flex items-center gap-1.5">
                            <span className="text-muted-foreground text-xs">Debt:</span>
                            {selectedDestination.debtSymbols.map((symbol) => (
                              <div key={symbol} className="flex items-center gap-1">
                                <TokenIcon token={{ symbol: TokenSymbol(symbol) }} className="h-3.5 w-3.5" />
                                <span className="text-muted-foreground text-xs">{symbol}</span>
                              </div>
                            ))}
                            {selectedDestination.marketName && (
                              <span className="text-muted-foreground text-xs">{selectedDestination.marketName}</span>
                            )}
                            {selectedDestination.healthFactor !== null && (
                              <span
                                className={cn(
                                  'text-xs',
                                  selectedDestination.healthFactor >= 3
                                    ? 'text-teal-400'
                                    : selectedDestination.healthFactor >= 2
                                      ? 'text-amber-400'
                                      : 'text-red-400',
                                )}
                              >
                                HF{' '}
                                {selectedDestination.healthFactor >= 4
                                  ? '4+'
                                  : selectedDestination.healthFactor.toFixed(2)}
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-muted-foreground text-xs">No debt</span>
                        )}
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="flex h-8 w-8 items-center justify-center rounded-full border border-white/10 bg-white/5">
                        <ChevronDown className="h-4 w-4 text-muted-foreground" />
                      </div>
                      <div className="flex flex-col items-start">
                        <span className="text-muted-foreground text-xs">Deposit to</span>
                        <span className="text-muted-foreground text-sm">Select position</span>
                      </div>
                    </>
                  )}
                </div>
                <ChevronDown className="h-4 w-4 shrink-0 text-white/50" />
              </button>

              <DialogContent
                className="md:data-[state=closed]:slide-out-to-top-[16px] md:data-[state=open]:slide-in-from-top-[16px] border border-border bg-background px-6 pt-6 md:top-24 md:min-w-0 md:max-w-md md:pb-2"
                aria-describedby={undefined}
                contentVerticalPosition="top"
                overlayClassName="z-[240]"
                closeClassName="top-6"
              >
                <div className="mb-4">
                  <DialogTitle className="mb-2">
                    <Typography variant="p" className="font-bold text-lg" gradient>
                      Select destination
                    </Typography>
                  </DialogTitle>
                </div>

                <div className="max-h-80 space-y-1 overflow-y-auto pb-4">
                  {isDestinationsLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                    </div>
                  ) : destinations.length === 0 ? (
                    <div className="p-4 text-center">
                      <Typography variant="prompt">No positions available</Typography>
                    </div>
                  ) : (
                    destinations.map((dest) => (
                      <button
                        key={dest.isNew ? 'new' : dest.id}
                        type="button"
                        className={cn(
                          'flex w-full items-center gap-3 rounded-lg p-3 transition-colors hover:bg-muted',
                          selectedDestination?.id === dest.id && !dest.isNew && 'bg-muted',
                          selectedDestination?.isNew && dest.isNew && 'bg-muted',
                        )}
                        onClick={() => handleSelectDestination(dest)}
                      >
                        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-white/10 bg-white/5">
                          {dest.isNew ? (
                            <Plus className="h-4 w-4 text-primary" />
                          ) : (
                            <span className="font-bold text-white/70 text-xs">#{dest.id}</span>
                          )}
                        </div>
                        <div className="flex flex-1 flex-col items-start gap-0.5">
                          <span className="font-medium text-sm text-white">{dest.label}</span>
                          {dest.isNew ? (
                            <span className="text-primary/70 text-xs">Creates a new sub-account</span>
                          ) : dest.debtSymbols.length > 0 ? (
                            <div className="flex w-full items-center justify-between">
                              <div className="flex items-center gap-1.5">
                                <span className="text-muted-foreground text-xs">Debt:</span>
                                {dest.debtSymbols.map((symbol) => (
                                  <div key={symbol} className="flex items-center gap-1">
                                    <TokenIcon token={{ symbol: TokenSymbol(symbol) }} className="h-4 w-4" />
                                    <span className="text-muted-foreground text-xs">{symbol}</span>
                                  </div>
                                ))}
                                {dest.marketName && (
                                  <span className="text-muted-foreground text-xs">{dest.marketName}</span>
                                )}
                                {dest.healthFactor !== null && (
                                  <span
                                    className={cn(
                                      'text-xs',
                                      dest.healthFactor >= 3
                                        ? 'text-teal-400'
                                        : dest.healthFactor >= 2
                                          ? 'text-amber-400'
                                          : 'text-red-400',
                                    )}
                                  >
                                    HF {dest.healthFactor >= 4 ? '4+' : dest.healthFactor.toFixed(2)}
                                  </span>
                                )}
                              </div>
                              <span className="text-muted-foreground text-xs">
                                ${dest.totalDebtUsd.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                              </span>
                            </div>
                          ) : (
                            <span className="text-muted-foreground text-xs">No debt</span>
                          )}
                        </div>
                      </button>
                    ))
                  )}
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {/* Swap details */}
          {isSwapFlow && swapQuote && (
            <div className="rounded-xl border border-blue-500/20 bg-blue-500/5 p-3">
              <div className="flex flex-col gap-2 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Swap via</span>
                  <span className="font-medium text-blue-400">
                    {swapQuote.route?.map((r: any) => r.providerName).join(' → ') || 'Pawswap'}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">You will receive</span>
                  <span className="font-medium text-white">
                    ~{Number(formatUnits(BigInt(swapQuote.amountOut), swapQuote.tokenOut.decimals)).toFixed(6)}{' '}
                    {swapQuote.tokenOut.symbol}
                  </span>
                </div>
                {swapQuote.amountOutMin && (
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">Min. after slippage</span>
                    <span className="text-muted-foreground">
                      {Number(formatUnits(BigInt(swapQuote.amountOutMin), swapQuote.tokenOut.decimals)).toFixed(6)}{' '}
                      {swapQuote.tokenOut.symbol}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Transaction Details */}
        <div className="mt-4 flex flex-col gap-2">
          {calculatedExpectedShares && (
            <TransactionOverviewDetailsItem label="You will receive">
              {Number(calculatedExpectedShares).toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 6,
              })}{' '}
              {evault.symbol}
            </TransactionOverviewDetailsItem>
          )}
          <TransactionOverviewDetailsItem label="Wallet Balance">
            {walletBalance.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 6,
            })}{' '}
            {activeSymbol}
          </TransactionOverviewDetailsItem>
          <TransactionOverviewDetailsItem label="Supply APY">
            <span className="inline-flex items-center gap-1">
              <span className="text-green-500">
                {evault.supplyAPY > 0 ? `${evault.supplyAPY.toFixed(2)}%` : '0.00%'}
              </span>
              {!isPreviewAPYLoading && previewNewAPY !== undefined && amountForSharesCalculation > 0n && (
                <ApyChange currentAPY={evault.supplyAPY} newAPY={previewNewAPY} type="earning" />
              )}
            </span>
          </TransactionOverviewDetailsItem>
          <SupplyCapDetail
            vaultAddress={evault.address}
            decimals={evault.decimals}
            symbol={evault.assetSymbol || evault.symbol}
          />

          {isNativeDeposit && (
            <div className="mt-2 rounded-lg bg-blue-500/10 p-3 text-blue-400 text-sm">
              Native HYPE will be wrapped to WHYPE and deposited in a single transaction.
            </div>
          )}
        </div>

        {/* Warnings */}
        {insufficientBalance && amountBigInt > 0n && (
          <div className="mt-4 rounded-lg border border-yellow-500/20 bg-yellow-500/10 p-3">
            <p className="text-sm text-yellow-400">
              Insufficient balance. You need{' '}
              {Number(effectiveDepositAmount).toLocaleString(undefined, { maximumFractionDigits: 6 })} {activeSymbol} but only
              have {walletBalance.toLocaleString(undefined, { maximumFractionDigits: 6 })} {activeSymbol}.
            </p>
          </div>
        )}

        {/* Timeout error display - shows when operation takes too long */}
        <TimeoutErrorDisplay
          isTimedOut={depositTimeout.isTimedOut}
          onRetry={handleExecuteWithTimeout}
          isRetryDisabled={isWriting || isWaiting || !simulationData?.request}
        />

        {/* Error display */}
        {displayError && !isTimeoutError(displayError) && (
          <TransactionErrorPanel
            error={displayError}
            showSimulationWarning={!!simulationError && !isUserRejection(simulationError)}
          />
        )}

        <BigBlocksBanner className="mt-4" />

        {/* Actions Grid (matching repay dialog) */}
        <div className="mt-6 grid grid-cols-[auto_auto_1fr_auto] gap-x-4">
          {/* Step 1: Approve Permit2 */}
          <ActionRow index={1}>
            <ActionRow.Icon path={assets.actions.approve} actionStatus={approveStatus} />
            <ActionRow.Title icon={<TokenIcon token={depositToken} className="h-6" />} actionStatus={approveStatus}>
              Approve Permit2
            </ActionRow.Title>
            <ActionRow.Description successMessage="Approved!" actionStatus={approveStatus} variant="compact" />
            <ActionRow.Action onAction={() => permit2.approvePermit2?.()} status={approveStatus}>
              Approve
            </ActionRow.Action>
          </ActionRow>

          {/* Step 2: Sign Permit */}
          <ActionRow index={2}>
            <ActionRow.Icon path={assets.actions.approve} actionStatus={signStatus} />
            <ActionRow.Title icon={<TokenIcon token={depositToken} className="h-6" />} actionStatus={signStatus}>
              Sign Permit
            </ActionRow.Title>
            <ActionRow.Description successMessage="Signed!" actionStatus={signStatus} variant="compact" />
            <ActionRow.Action onAction={() => permit2.signPermit2?.()} status={signStatus}>
              Sign
            </ActionRow.Action>
          </ActionRow>

          {/* Step 3: Deposit / Swap & Deposit */}
          <ActionRow index={3}>
            <ActionRow.Icon path={assets.actions.deposit} actionStatus={executeStatus} />
            <ActionRow.Title icon={<TokenIcon token={depositToken} className="h-6" />} actionStatus={executeStatus}>
              {isSwapFlow ? 'Swap & Deposit' : 'Deposit'}
            </ActionRow.Title>
            <ActionRow.Description
              successMessage={isSwapFlow ? 'Swapped & Deposited!' : 'Deposited!'}
              actionStatus={executeStatus}
              variant="compact"
            />
            <ActionRow.Action onAction={handleExecuteWithTimeout} status={executeStatus}>
              {depositTimeout.isTimedOut ? 'Retry' : isSwapFlow ? 'Swap & Deposit' : 'Deposit'}
            </ActionRow.Action>
          </ActionRow>
        </div>
      </DialogPanel>
    </MultiPanelDialog>
  )
}
